/** @jsxImportSource @emotion/react */
import {
  Button,
  Checkbox,
  Input,
  OTPInput,
  Snackbar,
  Typography,
  UtilityButton,
  createStyles,
  useTheme,
} from '@quickbit/qb-design-system';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUpdateEffect } from 'react-use';
import Reaptcha from 'reaptcha';
import { formatErrorResponse } from '../../../utils';
import { useLogin } from '../hooks/useLogin';

export const Login = () => {
  const style = useStyle();
  const navigate = useNavigate();
  const { spacing } = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [reCaptchaToken, setReCaptchaToken] = useState<string>();

  const captchaRef = useRef<Reaptcha>(null);

  const {
    loading,
    rememberMe,
    setRememberMe,
    inValidEmail,
    inValidPassword,
    inValidReCaptchaToken,
    loginUser,
    error,
    mfaToken,
    mfaCode,
    setMfaCode,
    verifyMfa,
    mfaLoading,
  } = useLogin(email, password, reCaptchaToken);

  useUpdateEffect(() => {
    setShowSnackbar(!!error);
  }, [error]);

  const handleLogin = () => {
    loginUser();
    setReCaptchaToken(undefined);
    void captchaRef.current?.reset();
  };

  return (
    <section css={style.container}>
      {mfaToken ? (
        <div css={style.card}>
          <div css={style.header}>
            <Typography variant="subtitle">
              Verify two-factor authenticator
            </Typography>
          </div>
          <Typography variant="body">
            Open your two-factor authenticator app or browser extension to view
            your authentication code.
          </Typography>
          <div css={{ marginTop: spacing.xxs }}>
            <OTPInput
              value={mfaCode}
              onChange={setMfaCode}
              error={error !== undefined}
              errorText="Two-factor authentication failed."
              onComplete={() => {
                if (!mfaLoading) {
                  void verifyMfa();
                }
              }}
            />
          </div>
        </div>
      ) : (
        <div css={style.card}>
          <div css={style.header}>
            <Typography variant="subtitle">Log in</Typography>
          </div>
          <form>
            <div css={style.field}>
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                label="Email"
                error={email.length !== 0 && inValidEmail}
                helperText={
                  email.length !== 0 && inValidEmail
                    ? 'Please enter a valid email'
                    : ''
                }
              />
            </div>
            <div css={style.field}>
              <Input
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                }}
                label="Password"
                type="password"
                error={password.length !== 0 && inValidPassword}
                helperText={
                  password.length !== 0 && inValidPassword
                    ? 'Password should contain 6 or more characters'
                    : ''
                }
              />
            </div>
            <div css={[style.field]}>
              <div css={style.rememberMeContainter}>
                <Checkbox
                  checked={rememberMe}
                  onChange={(checked: boolean) => setRememberMe(checked)}
                />
                <Typography variant="body">Remember me</Typography>
              </div>

              <UtilityButton
                variant="text"
                text="Forgot password"
                onClick={() =>
                  void navigate('/login/password-reset', { replace: true })
                }
              />
            </div>
            {!process.env.REACT_APP_DISABLE_RECAPTCHA && (
              <div css={style.reCaptcha}>
                <Reaptcha
                  ref={captchaRef}
                  onVerify={(res) => setReCaptchaToken(res)}
                  onExpire={() => setReCaptchaToken(undefined)}
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                />
              </div>
            )}
            <div css={style.field}>
              <Button
                disabled={
                  inValidEmail ||
                  inValidPassword ||
                  (!process.env.REACT_APP_DISABLE_RECAPTCHA &&
                    inValidReCaptchaToken) ||
                  loading
                }
                loading={loading}
                variant="primary"
                text="Login"
                onClick={handleLogin}
              />
            </div>
          </form>
        </div>
      )}
      <Snackbar
        message={error ? formatErrorResponse(error) : ''}
        variant="danger"
        onClose={() => setShowSnackbar(false)}
        open={showSnackbar}
      />
    </section>
  );
};

const useStyle = createStyles(({ spacing, palette, shape, sizing }) => ({
  container: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  card: {
    borderRadius: shape.borderRadius,
    border: `1px solid ${palette.grey.x100}`,
    background: palette.white,
    padding: spacing.xxs,
    width: 'fit-content',
    minWidth: sizing.small,
    maxWidth: sizing.medium,
  },
  header: { margin: `${spacing.xxxs} 0` },
  rememberMeContainter: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
  },
  field: {
    marginTop: spacing.xxs,
    display: 'flex',
  },
  reCaptcha: {
    marginTop: spacing.xxs,
  },
}));
